import { ReactNode } from 'react'

import { Country } from '@bemlo/enums'
import { Falsy } from '@bemlo/utils'

import { Grid } from './components/Grid'
import { SHIFT_HOURS } from './components/NewShift'
import { Shift } from './components/Shift'
import { TeamsLayout } from './components/TeamsLayout'
import { useCellTheme } from './components/useCellTheme'
import { VacanciesTitle } from './components/VacanciesTitle'
import { WeekTitle } from './components/WeekTitle'
import { DEFAULT_HEADER_COLUMN_WIDTH } from './consts'
import { Button } from './rows/Button'
import { DayDemand, DayDemandGraphData } from './rows/DayDemand'
import { Divider } from './rows/Divider'
import { DraftResource } from './rows/DraftResource'
import { ExternalResources } from './rows/ExternalResources'
import { FixedVacancies } from './rows/FixedVacancies'
import { FullSpan } from './rows/FullSpan'
import { Group } from './rows/Group'
import { Header } from './rows/Header'
import { FetchMore } from './rows/LoadMore'
import { Presentation } from './rows/Presentation'
import { Resource } from './rows/Resource'
import { ShiftDemands } from './rows/ShiftDemand'
import { Vacancies } from './rows/Vacancies'
import {
  AvailableShiftChannels,
  ScheduleZoom,
  UIAvailability,
  UICandidate,
  UIDraftShift,
  UIShift,
} from './types'
import {
  AvailabilityPopover,
  EnableEditing,
  EnableSelectDays,
  EnableSelectShifts,
  ScheduleProvider,
  ShiftPopover,
} from './useSchedule'
import {
  ShiftPreset,
  ShiftPresetInput,
  useShiftPresets,
} from './useShiftPresets'
import { useUIShiftsFromSlots } from './useUIShiftsFromSlots'
import { useAvailabilityStyle } from './utils/availability-style'
import { ScheduleDay, useDaysInRange } from './utils/get-days-in-range'
import { convertUIDraftShiftToShift } from './utils/ui-draft-shift-to-shift'
import { Div } from '../../../elements'

type ScheduleProps = {
  startsAt: number
  endsAt: number
  zoom?: ScheduleZoom
  headerColumnWidth?: number
  title?: ReactNode
  children?: ReactNode
  enableEditing?: EnableEditing | false
  enableSelectShifts?: EnableSelectShifts | false
  enableSelectDays?: EnableSelectDays | false
  compactBookings?: boolean
  availableChannels: AvailableShiftChannels | Falsy
  shiftPopover?: ShiftPopover
  availabilityPopover?: AvailabilityPopover
  shifts: UIShift[]
  availability?: UIAvailability[]
  resources?: UICandidate[]
  country?: Country
  exactDays?: boolean
}

const Schedule = (props: ScheduleProps) => {
  const [days] = useDaysInRange(
    props.startsAt,
    props.endsAt,
    props.country,
    props.exactDays ? null : undefined,
  )

  if (!days.length) {
    return null
  }

  return (
    <ScheduleProvider
      startsAt={props.startsAt}
      endsAt={props.endsAt}
      zoom={props.zoom ?? 'week'}
      headerColumnWidth={props.headerColumnWidth ?? DEFAULT_HEADER_COLUMN_WIDTH}
      days={days}
      enableEditing={props.enableEditing}
      enableSelectShifts={props.enableSelectShifts}
      enableSelectDays={props.enableSelectDays}
      availableChannels={props.availableChannels}
      compactBookings={props.compactBookings}
      shiftPopover={props.shiftPopover}
      availabilityPopover={props.availabilityPopover}
      shifts={props.shifts}
      resources={props.resources}
      availability={props.availability}
    >
      <Grid>
        <Header title={props.title} />
        <tbody>{props.children}</tbody>
      </Grid>
    </ScheduleProvider>
  )
}

const Vertical = (props: ScheduleProps) => {
  const [, weeks] = useDaysInRange(props.startsAt, props.endsAt, props.country)

  return (
    <Div>
      {weeks.map((week) => (
        <ScheduleProvider
          key={week.key}
          startsAt={props.startsAt}
          endsAt={props.endsAt}
          zoom={props.zoom ?? 'week'}
          headerColumnWidth={
            props.headerColumnWidth ?? DEFAULT_HEADER_COLUMN_WIDTH
          }
          days={week.days}
          enableEditing={props.enableEditing}
          enableSelectDays={props.enableSelectDays}
          enableSelectShifts={props.enableSelectShifts}
          availableChannels={props.availableChannels}
          compactBookings={props.compactBookings}
          shiftPopover={props.shiftPopover}
          availabilityPopover={props.availabilityPopover}
          shifts={props.shifts}
          availability={props.availability}
          resources={props.resources}
        >
          <Grid>
            <Header title={<WeekTitle week={week.number} />} />
            <tbody>{props.children}</tbody>
          </Grid>
        </ScheduleProvider>
      ))}
    </Div>
  )
}

Schedule.Vertical = Vertical
Schedule.Group = Group
Schedule.Divider = Divider
Schedule.Button = Button
Schedule.Vacancies = Vacancies
Schedule.ShiftDemands = ShiftDemands
Schedule.DayDemand = DayDemand
Schedule.FixedVacancies = FixedVacancies
Schedule.DraftResource = DraftResource
Schedule.ExternalResources = ExternalResources
Schedule.Presentation = Presentation
Schedule.Shift = Shift
Schedule.Resource = Resource
Schedule.FetchMore = FetchMore
Schedule.FullSpan = FullSpan
Schedule.TeamsLayout = TeamsLayout

export {
  Schedule,
  SHIFT_HOURS,
  useUIShiftsFromSlots,
  convertUIDraftShiftToShift,
  useShiftPresets,
  useCellTheme,
  useDaysInRange,
  useAvailabilityStyle,
  VacanciesTitle,
}

export type {
  EnableEditing as ScheduleEnableEditing,
  EnableSelectShifts as ScheduleEnableSelect,
  EnableSelectDays as ScheduleEnableSelectDays,
  UIShift,
  UIAvailability,
  UIDraftShift,
  ShiftPreset,
  ShiftPresetInput,
  ScheduleDay,
  DayDemandGraphData,
  AvailableShiftChannels,
}
